import React, { useState } from "react";
import { PiClipboardText } from "react-icons/pi";
import { Col, Select, Row, Modal } from "antd";
import { ExpandOutlined } from '@ant-design/icons';
import {
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import dayjs from "dayjs";
import "../styles/Proprieta.css";
import VelocitaIcon from "../assets/icons/Velocita_icon.svg";
const Proprieta = ({
  arco,
  selectedFeature,
  availableDates,
  selectedDate,
  setSelectedDate,
  selectedData,
  radarSelected,
}) => {
  const [selectedRoadIndex, setSelectedRoadIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if arco is an array with multiple roads or a single object
  const isMultipleRoads = arco && Array.isArray(arco) && arco.length > 0;

  // Group by hash if it's an array
  let roads = [];
  if (isMultipleRoads) {
    const roadsByHash = {};
    arco.forEach((element) => {
      const hash = element.hash;
      if (!roadsByHash[hash]) {
        roadsByHash[hash] = [];
      }
      roadsByHash[hash].push(element);
    });

    // Get unique roads
    roads = Object.keys(roadsByHash).map((hash) => ({
      hash,
      elements: roadsByHash[hash],
      // Use most recent data point for display
      data: roadsByHash[hash][roadsByHash[hash].length - 1],
    }));
  } else if (arco && !Array.isArray(arco)) {
    // Single road object
    roads = [{ hash: arco.hash, elements: [arco], data: arco }];
  }

  // Get available dates from roads data or use provided prop
  const getAvailableDatesFromRoads = () => {
    if (!roads || roads.length === 0) return [];

    const dates = new Set();
    roads.forEach(road => {
      road.elements?.forEach(el => {
        if (el.date) {
          dates.add(dayjs(el.date).format('YYYY-MM-DD'));
        }
      });
    });

    return Array.from(dates).sort();
  };

  const datesToDisplay = availableDates && availableDates.length > 0
    ? availableDates
    : getAvailableDatesFromRoads();

  // Set first date as default if no date is selected
  React.useEffect(() => {
    if (!selectedDate && datesToDisplay.length > 0) {
      setSelectedDate(datesToDisplay[0]);
    }
  }, [datesToDisplay, selectedDate, setSelectedDate]);

  // Handle case where only radar is selected (no roads)
  if (!arco || (Array.isArray(arco) && arco.length === 0)) {
    if (radarSelected) {
      // Radar-only view
      const radarProperties = [
        { label: "Tipo", value: "Stazione di monitoraggio ambientale" },
        { label: "Posizione", value: "44.40838, 8.906963" },
        {
          label: "Sensori",
          value: "CO2, PM2.5, PM10, NO2, Temp, Umidità, Pressione",
        },
        { label: "Fonte dati", value: "ambientali_finale_tutto" },
        { label: "Frequenza acquisizione", value: "Oraria" },
      ];

      return (
        <div className="proprieta-wrapper">
          <div className="proprieta-card">
            <div className="proprieta-header">
              <div className="proprieta-title">
                <PiClipboardText className="proprieta-icon" />
                <h3>Proprietà: Stazione di monitoraggio</h3>
              </div>
            </div>
            <div className="proprieta-content">
              {radarProperties.map((prop, index) => (
                <div key={index} className="proprieta-item">
                  <span className="proprieta-label">{prop.label}</span>
                  <span className="proprieta-value">{prop.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="proprieta-no-data">
        <p>Seleziona un arco sulla mappa o un radar per vedere i dettagli.</p>
      </div>
    );
  }

  // Generate chart data from selected road elements
  const generateChartData = () => {
    if (!isMultipleRoads || !selectedDate) {
      return selectedData || [];
    }

    const roadElements = roads[selectedRoadIndex]?.elements || [];

    // Filter by selected date if available
    const filteredElements = selectedDate
      ? roadElements.filter((el) => {
        if (el.date) {
          const elDate = dayjs(el.date).format("YYYY-MM-DD");
          return elDate === selectedDate;
        }
        return false;
      })
      : roadElements;

    // Process data points at 5-minute intervals
    const dataPoints = filteredElements.map((el) => {
      // Determine time key - Prioritize timestamp (Unix timestamp) as it's the most reliable
      let time;
      if (el.timestamp) {
        time = dayjs.unix(el.timestamp).format("HH:mm");
      } else if (el.datetime && !el.datetime.startsWith('1970')) {
        time = dayjs(el.datetime).format("HH:mm");
      } else if (el.hour !== undefined && el.hour < 24) {
        const minute = el.minute !== undefined ? el.minute : 0;
        time = `${el.hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      } else {
        return null;
      }

      // Calculate velocity (km/h) - support both new and old formats
      let velocita = 0;
      if (el.speed) {
        // New format has speed directly
        velocita = parseFloat(el.speed);
      } else if (el.length_in_meters && el.travel_time_in_seconds && el.travel_time_in_seconds > 0) {
        // Old format: calculate speed
        velocita = (el.length_in_meters / el.travel_time_in_seconds) * 3.6;
      }

      return {
        time,
        tempo_percorrenza: parseFloat(el.travel_time_in_seconds) || 0,
        tempo_storico: parseFloat(el.historic_traffic_travel_time_in_seconds) || 0,
        velocità: Math.round(velocita * 10) / 10,
        lunghezza_coda: parseFloat(el.traffic_length_in_meters) || 0,
      };
    }).filter(point => point !== null);

    // Sort by time
    dataPoints.sort((a, b) => {
      const [aHour, aMin] = a.time.split(':').map(Number);
      const [bHour, bMin] = b.time.split(':').map(Number);
      return (aHour * 60 + aMin) - (bHour * 60 + bMin);
    });

    return dataPoints;
  };

  const chartDataToDisplay = generateChartData();

  console.log("Proprieta - Chart data:", {
    isMultipleRoads,
    selectedRoadIndex,
    selectedDate,
    roadsCount: roads.length,
    chartDataLength: chartDataToDisplay.length,
    sampleData: chartDataToDisplay[0],
  });

  // Render chart component
  const renderChart = (height = 300) => (
    <>
      {selectedDate && chartDataToDisplay?.length > 0 ? (
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={chartDataToDisplay}>
            <defs>
              <linearGradient id="velocityGradientProp" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={selectedRoadIndex === 0 ? "#A1E430" : "#8B7FD9"} stopOpacity={0.3} />
                <stop offset="95%" stopColor={selectedRoadIndex === 0 ? "#A1E430" : "#8B7FD9"} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 11, fill: "#666" }}
              axisLine={{ stroke: "#e0e0e0" }}
              interval="preserveStartEnd"
              ticks={['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:59']}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#666" }}
              axisLine={{ stroke: "#e0e0e0" }}
              label={{
                value: "km/h",
                position: "insideLeft",
                style: { fontSize: 12, fill: "#666" },
              }}
            />
            <Tooltip />
            <Area
              type="monotone"
              dataKey="velocità"
              stroke={selectedRoadIndex === 0 ? "#A1E430" : "#8B7FD9"}
              strokeWidth={2}
              fill="url(#velocityGradientProp)"
              name={roads.length > 1 ? `Strada ${selectedRoadIndex + 1}` : 'Velocità'}
              connectNulls={true}
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : selectedDate ? (
        <div className="chart-no-data">
          <p>⚠️ Nessun dato disponibile per questa strada nella data selezionata.</p>
          <p style={{ fontSize: '12px', marginTop: '8px', color: '#999' }}>
            Prova a selezionare un'altra data.
          </p>
        </div>
      ) : (
        <div className="chart-no-data">
          <p>Seleziona una data per visualizzare il grafico.</p>
        </div>
      )}
    </>
  );

  // Check if road has any data at all
  const hasAnyData = datesToDisplay.length > 0;

  return (
    <>
      <div className="proprieta-wrapper">
        <div className="proprieta-title">
          <img src={VelocitaIcon} alt="Congestion" className="proprieta-icon-img" />
          <h3>Velocità</h3>
        </div>

        {/* Road selector buttons - always visible when 2 roads are selected */}
        {roads.length > 1 && (
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px 0',
            marginBottom: '8px'
          }}>
            <button
              onClick={() => setSelectedRoadIndex(0)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: selectedRoadIndex === 0 ? '2px solid #A1E430' : '1px solid #ddd',
                background: selectedRoadIndex === 0 ? '#A1E43020' : 'white',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: selectedRoadIndex === 0 ? 'bold' : 'normal'
              }}
            >
              Strada 1
            </button>
            <button
              onClick={() => setSelectedRoadIndex(1)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: selectedRoadIndex === 1 ? '2px solid #8B7FD9' : '1px solid #ddd',
                background: selectedRoadIndex === 1 ? '#8B7FD920' : 'white',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: selectedRoadIndex === 1 ? 'bold' : 'normal'
              }}
            >
              Strada 2
            </button>
          </div>
        )}

        {/* Always show date selector and chart area */}
        <div className="proprieta-content anomaly-content">
          <p className="proprieta-description anomaly-description">
            Seleziona la data per la quale vuoi visualizzare i valori.
          </p>
          {hasAnyData ? (
            <Select
              placeholder="Seleziona data"
              className="anomaly-date-picker"
              value={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              getPopupContainer={(trigger) => trigger.parentElement}
              popupStyle={{ zIndex: 9999 }}
              style={{  borderRadius: "20px" }}
            >
              {datesToDisplay.map(date => (
                <Select.Option key={date} value={date}>
                  {dayjs(date).format('DD/MM/YYYY')}
                </Select.Option>
              ))}
            </Select>
          ) : (
            <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
              Nessuna data disponibile per questa strada.
            </div>
          )}
        </div>

        <div style={{ position: 'relative' }}>
          {selectedDate && chartDataToDisplay?.length > 0 && (
            <button
              onClick={() => setIsExpanded(true)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                width: '36px',
                height: '36px',
                backgroundColor: 'rgba(24, 144, 255, 0.9)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                zIndex: 100,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(64, 169, 255, 1)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.9)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title="Espandi grafico"
            >
              <ExpandOutlined />
            </button>
          )}
          {renderChart()}
        </div>
      </div>

      {/* Expanded Chart Modal */}
      <Modal
        open={isExpanded}
        onCancel={() => setIsExpanded(false)}
        footer={null}
        width="95%"
        centered
        title="Velocità - Vista Espansa"
        destroyOnClose
        zIndex={9999}
        style={{ top: 20 }}
      >
        <div style={{ padding: '20px 0' }}>
          {renderChart(500)}
        </div>
      </Modal>
    </>
  );
};

export default Proprieta;
